import React from 'react';
import {
  Dashboard as DashboardIcon,
  People as PeopleIcon,
  Event as EventIcon,
  AccountBalance as AccountBalanceIcon,
  AdminPanelSettings as AdminIcon,
  Assessment as AssessmentIcon,
  Code as CodeIcon,
  Build as BuildIcon,
  IntegrationInstructions as IntegrationIcon,
  Group as GroupIcon,

  ViewModule as ViewModuleIcon,
  Assignment as AssignmentIcon,
  RecordVoiceOver as RecordVoiceOverIcon,
  Timeline as TimelineIcon,
  Biotech as BiotechIcon,
  Article as ArticleIcon,
  Gavel as GavelIcon,
  LocalHospital as LocalHospitalIcon,
  LocalPharmacy as LocalPharmacyIcon,
  Medication as MedicationIcon,
  Chat as ChatIcon,
  ViewList as ViewListIcon,
  PlaylistAddCheck as PlaylistAddCheckIcon,
  FactCheck as FactCheckIcon,
  SyncAlt as SyncAltIcon,
  CalendarToday as CalendarIcon,
  Warning as WarningIcon,
  AssignmentInd as AssignmentIndIcon,
  ExitToApp as ExitToAppIcon,
  ViewQuilt as ViewQuiltIcon,
  MeetingRoom as RoomIcon,
  HealthAndSafety as HealthAndSafetyIcon,
  Analytics as AnalyticsIcon,
  VerifiedUser as ComplianceIcon,
  Science as OncologyIcon,
  Healing as HealingIcon,
  MonitorHeart as MonitorHeartIcon,
  Groups as TumorBoardIcon,
  Science as TrialIcon,
  Favorite as SurvivorshipIcon,
  TrendingUp as PredictiveIcon,
} from '@mui/icons-material';
import { UserRole } from '../../types/enums';

export const createNavigationLinks = (t, shouldShowTriage) => {
  const baseLinks = [
    { 
      text: 'Dashboard', 
      icon: <DashboardIcon />, 
      path: '/dashboard', 
      roles: ['admin', 'doctor', 'nurse', 'facility_admin', 'billing'] 
    },
    {
      text: 'Revenue Management',
      icon: <AccountBalanceIcon />,
      key: 'revenue',
      roles: ['admin', 'doctor', 'nurse', 'facility_admin', 'billing'],
      children: [
        { text: t('navigation.dashboard'), path: '/revenue/dashboard', icon: <DashboardIcon /> },
        { text: 'Surgery Dashboard', path: '/revenue/surgery-dashboard', icon: <RoomIcon /> },
      ]
    },
    { 
      text: t('navigation.patients'), 
      icon: <PeopleIcon />, 
      path: '/patients', 
      roles: ['admin', 'doctor', 'nurse', 'facility_admin'] 
    },
    { 
      text: t('navigation.appointments'), 
      icon: <EventIcon />, 
      path: '/appointments', 
      roles: ['admin', 'doctor', 'nurse', 'facility_admin'] 
    },
    {
      text: t('navigation.calendar'),
      icon: <CalendarIcon />,
      key: 'calendar',
      roles: ['admin', 'doctor', 'nurse', 'facility_admin'],
      children: [
        { text: 'Calendar View', path: '/calendar', icon: <CalendarIcon /> },
        { 
          text: 'Timesheet Report', 
          path: '/calendar/audit-reports', 
          icon: <GavelIcon />, 
          roles: [UserRole.ADMIN, UserRole.FACILITY_ADMIN] 
        },
      ]
    },
    { 
      text: t('navigation.billing'), 
      icon: <AccountBalanceIcon />, 
      path: '/billing', 
      roles: ['admin', 'billing', 'facility_admin'] 
    },
    { 
      text: t('navigation.encounters'), 
      icon: <LocalHospitalIcon />, 
      path: '/encounters', 
      roles: ['doctor', 'nurse', 'admin', 'facility_admin'] 
    },
  ];

  // Conditionally add Triage Board if facility supports Emergency Visit
  if (shouldShowTriage) {
    baseLinks.push({ 
      text: 'Triage Board', 
      icon: <ViewQuiltIcon />, 
      path: '/triage-board', 
      roles: ['doctor', 'nurse', 'admin', 'facility_admin'] 
    });
  }

  const remainingLinks = [
    {
      text: 'Surgical Facilities',
      icon: <RoomIcon />,
      key: 'surgical-facilities',
      roles: ['admin', 'doctor', 'nurse', 'facility_admin'],
      path: '/surgical-facilities',
    },
    {
      text: 'Inpatient',
      icon: <LocalHospitalIcon />,
      key: 'inpatient',
      roles: ['admin', 'doctor', 'nurse', 'facility_admin'],
      children: [
        { text: 'Census', path: '/inpatient/list', icon: <PlaylistAddCheckIcon /> },
        { text: 'Admit', path: '/inpatient/admit', icon: <AssignmentIndIcon /> },
        { text: 'Discharge', path: '/inpatient/discharge', icon: <ExitToAppIcon /> },
        { text: 'Transfer', path: '/inpatient/transfer', icon: <SyncAltIcon /> },
        { text: 'Bed Assignment', path: '/inpatient/bed-assignment', icon: <AssignmentIcon /> },
        { text: 'Nursing Flowsheet', path: '/inpatient/flowsheet', icon: <TimelineIcon /> },
      ]
    },
    {
      text: 'Oncology',
      icon: <OncologyIcon />,
      key: 'oncology',
      roles: ['admin', 'doctor', 'nurse', 'facility_admin'],
      children: [
        { text: 'Dashboard', path: '/oncology', icon: <DashboardIcon /> },
        { text: 'Episodes of Care', path: '/oncology/episodes', icon: <TimelineIcon /> },
        { text: 'Cancer Registry', path: '/oncology/diagnoses', icon: <AssignmentIcon /> },
        { text: 'Chemotherapy', path: '/oncology/chemotherapy', icon: <MedicationIcon /> },
        { text: 'Tumor Board', path: '/oncology/tumor-board', icon: <TumorBoardIcon /> },
        { text: 'Clinical Trials', path: '/oncology/clinical-trials', icon: <TrialIcon /> },
        { text: 'Toxicity Management', path: '/oncology/toxicity', icon: <WarningIcon /> },
        { text: 'Survivorship Care', path: '/oncology/survivorship', icon: <HealingIcon /> },
      ]
    },
    { 
      text: t('navigation.prescriptions'), 
      icon: <MedicationIcon />, 
      path: '/prescriptions', 
      roles: ['doctor', 'nurse', 'admin', 'facility_admin'] 
    },
    { 
      text: t('navigation.pharmacy'), 
      icon: <LocalPharmacyIcon />, 
      path: '/admin/pharmacies', 
      roles: ['admin'] 
    },
    { 
      text: t('navigation.messaging'), 
      icon: <ChatIcon />, 
      path: '/messaging', 
      roles: ['admin', 'doctor', 'nurse', 'facility_admin'] 
    },
    {
      text: t('navigation.bi_reporting'),
      icon: <AnalyticsIcon />,
      key: 'bi-reporting',
      roles: ['admin', 'doctor', 'nurse', 'facility_admin'],
      children: [
        { text: 'Compliance', path: '/bi-reporting/compliance', icon: <ComplianceIcon /> },
        { text: 'Predictive Analytics', path: '/bi-reporting/predictive', icon: <PredictiveIcon /> },
        { text: 'Care Gaps', path: '/bi-reporting/care-gaps', icon: <HealthAndSafetyIcon /> },
        { text: 'Industry Benchmarks', path: '/bi-reporting/benchmarks', icon: <AnalyticsIcon /> },
      ]
    },
    { 
      text: t('navigation.care_coordination'), 
      icon: <HealthAndSafetyIcon />, 
      path: '/carecoordination', 
      roles: ['admin', 'doctor', 'nurse', 'facility_admin'] 
    },

    { 
      text: t('navigation.admin.main'), 
      icon: <AdminIcon />, 
      key: 'admin',
      roles: ['admin', 'facility_admin'], 
      children: [
        { text: t('navigation.admin.allergies_metadata'), path: '/admin/allergies-metadata', icon: <WarningIcon /> },
        { text: t('navigation.admin.codes'), path: '/admin/codes', icon: <CodeIcon /> },
        { text: t('navigation.admin.compliance_audit'), path: '/admin/compliance-audit', icon: <ComplianceIcon /> },
        { text: t('navigation.admin.features'), path: '/admin/features', icon: <BuildIcon /> },
        { text: t('navigation.admin.forms'), path: '/admin/forms', icon: <ArticleIcon /> },
        { text: t('navigation.admin.groups'), path: '/admin/facility-groups', icon: <ViewModuleIcon /> },
        { text: t('navigation.admin.inpatient_workflow'), path: '/admin/inpatient-workflow', icon: <FactCheckIcon /> },
        { text: t('navigation.admin.integrations'), path: '/admin/third-party-integrations', icon: <IntegrationIcon /> },
        { text: 'Lab Test Harness', path: '/admin/lab-test-harness', icon: <BiotechIcon /> },
        { text: t('navigation.admin.macros'), path: '/admin/macros', icon: <RecordVoiceOverIcon /> },
        { text: t('navigation.admin.pharmacies'), path: '/admin/pharmacies', icon: <LocalPharmacyIcon /> },
        { text: t('navigation.admin.reports'), path: '/admin/reports', icon: <AssessmentIcon /> },

        { text: t('navigation.admin.transmission_log'), path: '/admin/transmission-log', icon: <TimelineIcon /> },
        { text: t('navigation.admin.users'), path: '/admin/users', icon: <GroupIcon /> },
      ]
    },
    {
      text: t('navigation.procedures.main'),
      icon: <BiotechIcon />,
      key: 'procedures',
      roles: ['admin', 'doctor', 'nurse'],
      children: [
        { text: t('navigation.procedures.orders'), path: '/procedures', icon: <AssignmentIcon /> },
        { text: t('navigation.procedures.test_panels'), path: '/procedures/test-panels', icon: <ViewListIcon /> },
        { text: t('navigation.procedures.test_definitions'), path: '/procedures/test-definitions', icon: <PlaylistAddCheckIcon /> },
        { text: t('navigation.procedures.results'), path: '/procedures/results', icon: <FactCheckIcon /> },
        { text: t('navigation.procedures.order_status'), path: '/procedures/order-status', icon: <SyncAltIcon /> },
      ]
    }
  ];

  return [...baseLinks, ...remainingLinks];
}; 